import { useEffect, useRef, useState } from "react";
import {
  Settings as SettingsIcon,
  UserRound,
  Camera,
  Loader2,
  Share2,
  ShieldAlert,
  KeyRound,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  getBoideToken,
  getProfile,
  updateProfile,
  uploadAvatar,
  type BoideProfile,
  type SocialLinks,
} from "@/lib/boide-api";
import { refreshBoideUser } from "@/hooks/use-boide-user";
import { AvatarCropDialog } from "@/components/avatar-crop-dialog";
import { DeleteAccountDialog } from "@/components/delete-account-dialog";
import { ChangePasswordDialog } from "@/components/auth/change-password-dialog";
import { AiContextSettingsSection } from "@/components/ai-context-settings";

const SOCIAL_FIELDS: { key: keyof SocialLinks; label: string; placeholder: string }[] = [
  { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/..." },
  { key: "zalo", label: "Zalo", placeholder: "Số Zalo hoặc link" },
  { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/..." },
  { key: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/@..." },
  { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@..." },
];

export function BoideSettingsDialog({
  trigger,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  firstTime = false,
  onSaved,
}: {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (v: boolean) => void;
  firstTime?: boolean;
  /** Gọi sau khi lưu hồ sơ thành công (để nơi mở dialog làm mới dữ liệu). */
  onSaved?: () => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange !== undefined ? externalOnOpenChange : setInternalOpen;
  const [profile, setProfile] = useState<Partial<BoideProfile>>({});
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<RequiredFieldErrors>({});
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  // Chỉ biết được sau khi mount (localStorage) — tránh lệch SSR/hydration.
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFieldErrors({});
    setIsLoggedIn(Boolean(getBoideToken()));
    void getProfile()
      .then((data) => setProfile(data))
      .catch(() => setProfile({}));
  }, [open]);

  const updateProfileField = (key: keyof BoideProfile, value: string) => {
    setProfile((current) => ({ ...current, [key]: value }));
    if (key in fieldErrors) {
      setFieldErrors(({ [key as RequiredField]: _cleared, ...rest }) => rest);
    }
  };

  const updateSocial = (key: keyof SocialLinks, value: string) => {
    setProfile((current) => ({
      ...current,
      social_links: { ...(current.social_links || {}), [key]: value },
    }));
  };

  const onPickAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Vui lòng chọn file ảnh");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setPendingAvatarFile(file);
    setCropOpen(true);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onConfirmCroppedAvatar = async (croppedFile: File) => {
    setUploadingAvatar(true);
    try {
      const { avatar } = await uploadAvatar(croppedFile);
      setProfile((current) => ({ ...current, avatar }));
      await refreshBoideUser();
      toast.success("Đã cập nhật ảnh đại diện");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Tải ảnh thất bại");
    } finally {
      setUploadingAvatar(false);
      setPendingAvatarFile(null);
    }
  };

  const save = async () => {
    // Chặn sớm các trường bắt buộc: báo đủ mọi trường thiếu cùng lúc (BE cũng từ chối khi thiếu
    // ngày sinh) — vừa toast, vừa viền đỏ + dòng lỗi dưới từng trường.
    if (getBoideToken()) {
      const errors = validateRequiredFields(profile);
      setFieldErrors(errors);
      const missing = Object.values(errors);
      if (missing.length > 0) {
        toast.error(`Vui lòng bổ sung: ${missing.join(", ")}`);
        return;
      }
    }
    setSaving(true);
    try {
      if (getBoideToken()) {
        // Bỏ các social link rỗng để không ghi đè bằng chuỗi trống.
        const social = Object.fromEntries(
          Object.entries(profile.social_links || {}).filter(([, v]) => v && v.trim()),
        );
        await updateProfile({
          real_name: profile.real_name || null,
          birth_date: profile.birth_date || null,
          birth_time: profile.birth_time || null,
          birth_calendar: profile.birth_calendar || "solar",
          gender: profile.gender || null,
          birth_location: profile.birth_location || null,
          current_location: profile.current_location || null,
          occupation: profile.occupation || null,
          life_goals: profile.life_goals || null,
          phone: profile.phone || null,
          social_links: social,
        });
        await refreshBoideUser();
      }

      onSaved?.();
      toast.success("Đã lưu tài khoản và hồ sơ");
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Không lưu được cấu hình");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {externalOpen === undefined && (
        <DialogTrigger asChild>
          {trigger ?? (
            <Button variant="ghost" size="sm" className="gap-2">
              <SettingsIcon className="h-4 w-4" />
              Tài khoản
            </Button>
          )}
        </DialogTrigger>
      )}
      {/* p-0 + flex-col: body cuộn riêng, header/footer đứng yên → nút Bỏ qua/Lưu luôn thấy */}
      <DialogContent
        hideClose={firstTime}
        className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-2xl"
      >
        {/* Màn chào mừng: thay dấu X bằng nút "Bỏ qua" nổi bật, user thấy ngay lối ra */}
        {firstTime && (
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute right-4 top-4 z-20 rounded-full border border-gold-soft bg-primary/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary shadow-gold transition hover:bg-primary/20 active:scale-95"
          >
            Bỏ qua
          </button>
        )}
        <DialogHeader className={firstTime ? "px-6 pb-2 pr-24 pt-6" : "px-6 pb-2 pt-6"}>
          <DialogTitle className="font-display tracking-[0.2em] text-gold-gradient">
            {firstTime ? "CHÀO MỪNG — HOÀN THIỆN HỒ SƠ" : "TÀI KHOẢN & HỒ SƠ"}
          </DialogTitle>
          <DialogDescription className="font-serif-soft italic">
            {firstTime
              ? "Chỉ cần họ tên, ngày sinh và giới tính để thầy luận giải chính xác hơn — bạn có thể bỏ qua và bổ sung sau trong Tài khoản & Hồ sơ."
              : "Cập nhật thông tin cá nhân dùng cho Tử Vi, luận giải và cấu hình phiên đăng nhập."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-3">
          <section className="space-y-3 rounded-lg border border-gold-soft/60 bg-background/30 p-4">
            <Label className="flex items-center gap-2 text-xs uppercase tracking-widest">
              <UserRound className="h-3.5 w-3.5 text-primary" /> Hồ sơ cá nhân
            </Label>

            {/* Ảnh đại diện — ẩn ở màn chào mừng để form ngắn, dễ bỏ qua */}
            {!firstTime && (
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20 border-2 border-gold-soft/50">
                  {profile.avatar ? <AvatarImage src={profile.avatar} alt="avatar" /> : null}
                  <AvatarFallback className="bg-gold-gradient text-xl text-primary-foreground">
                    {(profile.real_name || profile.full_name || "?").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onPickAvatar}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingAvatar}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadingAvatar ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="mr-2 h-4 w-4" />
                    )}
                    Đổi ảnh đại diện
                  </Button>
                  <p className="mt-1 text-[11px] text-muted-foreground">JPG/PNG, tối đa ~5MB.</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ProfileField label="Họ tên thật" required error={fieldErrors.real_name}>
                <Input
                  aria-invalid={Boolean(fieldErrors.real_name)}
                  className="aria-[invalid=true]:border-red-500"
                  value={profile.real_name ?? ""}
                  onChange={(e) => updateProfileField("real_name", e.target.value)}
                  placeholder="Nhập họ tên"
                />
              </ProfileField>
              {!firstTime && (
                <ProfileField label="Nghề nghiệp">
                  <Input
                    value={profile.occupation ?? ""}
                    onChange={(e) => updateProfileField("occupation", e.target.value)}
                    placeholder="Nhập nghề nghiệp"
                  />
                </ProfileField>
              )}
              <ProfileField label="Ngày sinh" required error={fieldErrors.birth_date}>
                <Input
                  aria-invalid={Boolean(fieldErrors.birth_date)}
                  className="aria-[invalid=true]:border-red-500"
                  type="date"
                  value={profile.birth_date ?? ""}
                  onChange={(e) => updateProfileField("birth_date", e.target.value)}
                />
              </ProfileField>
              <ProfileField label="Loại lịch sinh">
                <select
                  value={profile.birth_calendar ?? "solar"}
                  onChange={(e) => updateProfileField("birth_calendar", e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                >
                  <option value="solar" className="bg-popover text-foreground">
                    Ngày dương lịch
                  </option>
                  <option value="lunar" className="bg-popover text-foreground">
                    Ngày âm lịch
                  </option>
                </select>
              </ProfileField>
              <ProfileField label="Giới tính" required error={fieldErrors.gender}>
                <select
                  value={profile.gender ?? ""}
                  onChange={(e) => updateProfileField("gender", e.target.value)}
                  aria-invalid={Boolean(fieldErrors.gender)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary aria-[invalid=true]:border-red-500"
                >
                  <option value="" className="bg-popover text-foreground">
                    Chọn giới tính
                  </option>
                  <option value="male" className="bg-popover text-foreground">
                    Nam
                  </option>
                  <option value="female" className="bg-popover text-foreground">
                    Nữ
                  </option>
                  <option value="other" className="bg-popover text-foreground">
                    Khác
                  </option>
                </select>
              </ProfileField>
              <ProfileField label="Giờ sinh (không bắt buộc)">
                <Input
                  type="time"
                  value={profile.birth_time ?? ""}
                  onChange={(e) => updateProfileField("birth_time", e.target.value)}
                />
              </ProfileField>
              {!firstTime && (
                <>
                  <ProfileField label="Nơi sinh">
                    <Input
                      value={profile.birth_location ?? ""}
                      onChange={(e) => updateProfileField("birth_location", e.target.value)}
                      placeholder="Nhập nơi sinh"
                    />
                  </ProfileField>
                  <ProfileField label="Nơi ở hiện tại">
                    <Input
                      value={profile.current_location ?? ""}
                      onChange={(e) => updateProfileField("current_location", e.target.value)}
                      placeholder="Nhập nơi ở hiện tại"
                    />
                  </ProfileField>
                  <ProfileField label="Số điện thoại">
                    <Input
                      value={profile.phone ?? ""}
                      onChange={(e) => updateProfileField("phone", e.target.value)}
                      placeholder="VD: 0901234567"
                    />
                  </ProfileField>
                </>
              )}
            </div>
            {!firstTime && (
              <ProfileField label="Mục tiêu / bối cảnh cá nhân">
                <textarea
                  value={profile.life_goals ?? ""}
                  onChange={(e) => updateProfileField("life_goals", e.target.value)}
                  rows={3}
                  className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                  placeholder="Mục tiêu, điều đang quan tâm, bối cảnh muốn thầy lưu ý..."
                />
              </ProfileField>
            )}
            {firstTime && (
              <p className="text-[11px] text-muted-foreground">
                Không nhớ giờ sinh? Cứ để trống. Các thông tin khác bổ sung sau trong mục Tài khoản
                &amp; Hồ sơ.
              </p>
            )}
          </section>

          {/* Mạng xã hội — ẩn ở màn chào mừng */}
          {!firstTime && (
            <section className="space-y-3 rounded-lg border border-gold-soft/60 bg-background/30 p-4">
              <Label className="flex items-center gap-2 text-xs uppercase tracking-widest">
                <Share2 className="h-3.5 w-3.5 text-primary" /> Mạng xã hội
              </Label>
              <p className="text-[11px] text-muted-foreground">
                Dùng để kết nối khi se duyên / hợp tác (chỉ hiển thị khi bạn chọn chia sẻ).
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {SOCIAL_FIELDS.map((s) => (
                  <ProfileField key={s.key} label={s.label}>
                    <Input
                      value={profile.social_links?.[s.key] ?? ""}
                      onChange={(e) => updateSocial(s.key, e.target.value)}
                      placeholder={s.placeholder}
                    />
                  </ProfileField>
                ))}
              </div>
            </section>
          )}

          {/* Dữ kiện cá nhân thầy được dùng — mỗi công tắc tự lưu, mặc định tắt hết */}
          {!firstTime && isLoggedIn && <AiContextSettingsSection />}

          {/* Bảo mật — chỉ hiện khi đã đăng nhập, ẩn ở màn chào mừng */}
          {!firstTime && isLoggedIn && (
            <section className="space-y-3 rounded-lg border border-gold-soft/60 bg-background/30 p-4">
              <Label className="flex items-center gap-2 text-xs uppercase tracking-widest">
                <KeyRound className="h-3.5 w-3.5 text-primary" /> Bảo mật
              </Label>
              <p className="text-[11px] text-muted-foreground">
                Đặt mật khẩu để đăng nhập bằng email, không phụ thuộc vào Google/Apple. Đổi mật khẩu
                sẽ đăng xuất mọi thiết bị.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPasswordOpen(true)}
              >
                <KeyRound className="mr-2 h-4 w-4" />
                Mật khẩu đăng nhập
              </Button>
            </section>
          )}

          {/* Vùng nguy hiểm — chỉ hiện khi đã đăng nhập, ẩn ở màn chào mừng */}
          {!firstTime && isLoggedIn && (
            <section className="space-y-3 rounded-lg border border-destructive/50 bg-destructive/5 p-4">
              <Label className="flex items-center gap-2 text-xs uppercase tracking-widest text-destructive">
                <ShieldAlert className="h-3.5 w-3.5" /> Vùng nguy hiểm
              </Label>
              <p className="text-[11px] text-muted-foreground">
                Xoá tài khoản là <strong className="text-destructive">không thể hoàn tác</strong>:
                mất toàn bộ số dư (không hoàn tiền), lịch sử luận giải, lá số, chuỗi điểm danh và
                hoa hồng giới thiệu. Chỉ đăng ký lại được bằng email này sau 90 ngày.
              </p>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Xoá tài khoản
              </Button>
            </section>
          )}
        </div>

        <DialogFooter className="gap-2 border-t border-gold-soft/40 px-6 py-3">
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {firstTime ? "Bỏ qua — điền sau" : "Hủy"}
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Đang lưu..." : "Lưu"}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AvatarCropDialog
        open={cropOpen}
        onOpenChange={setCropOpen}
        file={pendingAvatarFile}
        saving={uploadingAvatar}
        onConfirm={onConfirmCroppedAvatar}
      />

      <ChangePasswordDialog
        open={passwordOpen}
        onOpenChange={setPasswordOpen}
        onChanged={() => setOpen(false)}
      />

      <DeleteAccountDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={() => setOpen(false)}
      />
    </Dialog>
  );
}

type RequiredField = "real_name" | "birth_date" | "gender";
type RequiredFieldErrors = Partial<Record<RequiredField, string>>;

const REQUIRED_FIELD_LABELS: Record<RequiredField, string> = {
  real_name: "Họ tên",
  birth_date: "Ngày sinh",
  gender: "Giới tính",
};

/** Trả về map trường bắt buộc đang thiếu → nhãn hiển thị (rỗng nếu hợp lệ). */
function validateRequiredFields(profile: Partial<BoideProfile>): RequiredFieldErrors {
  const isMissing: Record<RequiredField, boolean> = {
    real_name: !(profile.real_name ?? "").trim(),
    birth_date: !profile.birth_date,
    gender: !profile.gender,
  };
  return Object.fromEntries(
    (Object.keys(isMissing) as RequiredField[])
      .filter((key) => isMissing[key])
      .map((key) => [key, REQUIRED_FIELD_LABELS[key]]),
  );
}

function ProfileField({
  label,
  required = false,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  /** Nhãn trường bị thiếu — có giá trị thì hiện dòng lỗi đỏ bên dưới. */
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-primary/85">
        {label}
        {required && (
          <span aria-hidden className="ml-0.5 text-red-500">
            *
          </span>
        )}
      </span>
      {children}
      {error && <span className="text-[11px] text-red-500">Vui lòng nhập {error}</span>}
    </label>
  );
}
